import { forwardRef, RefObject, SVGProps } from 'react';
import { alpha, Box, styled } from '@mui/material';
import classNames from 'classnames';

import Bamse from '@assets/images/Bamse.svg?react';
import useStyleNames from '@hooks/useSectionStyleNames';
import { getAspectRatioStr, PromoImageType } from '@models/Image';
import { SectionStyleSegment } from '@models/PageSection';

import {
  getBaseHeight,
  groupStylePatternSegmentIconClasses,
  isSpecialShape,
  StylePatternSegmentProps,
} from './utils';

const aspectRatioMap: Record<SectionStyleSegment, PromoImageType> = {
  'banner-fullscreen': 'wide',
  'channel-ongoing': 'wide',
  'kids-service': 'square',
  'poster-notext': 'poster',
  'promo-wide-large': 'promo-wide-large',
  'promo-wide': 'promo-wide',
  'streaming-service': 'wide',
  'wide-notext-double': 'wide',
  'wide-notext-large': 'wide',
  app: 'wide',
  channel: 'wide',
  genre: 'wide',
  person: 'square',
  poster: 'poster',
  wide: 'wide',
  'wide-airtime': 'wide',
};

const Shape = styled(Box)<Pick<StylePatternSegmentProps, 'size'>>`
  --baseHeight: ${({ size }) => getBaseHeight(size)}px;

  background: ${({ theme }) => alpha(theme.palette.text.primary, 0.3)};
  border-radius: 1px;
  max-height: var(--baseHeight);
  max-width: var(--baseHeight);
  width: var(--baseHeight);

  &.${groupStylePatternSegmentIconClasses.discrete} {
    background: ${({ theme }) => theme.palette.background.paper};
  }

  &.person {
    border-radius: 50%;
  }

  &.channel-ongoing,
  &.channel,
  &.genre,
  &.person,
  &.wide,
  &.wide-airtime {
    width: calc(var(--baseHeight) / 3.5);
  }

  &.app,
  &.streaming-service {
    width: calc(var(--baseHeight) / 3.8);
  }

  &.poster,
  &.wide-notext-large,
  &.poster-notext {
    height: calc(var(--baseHeight) / 2.1);
    max-height: unset;
    max-width: unset;
    width: auto;
  }

  &.banner-fullscreen {
    max-width: calc(var(--baseHeight) * 1.2);
    width: calc(var(--baseHeight) * 1.2);
  }

  &.promo-wide {
    // Pattern secific styling
  }

  &.promo-wide-large {
    // Pattern secific styling
  }

  &.wide-notext-double {
    // Pattern secific styling
  }
`;

const StyledBamse = styled(Bamse)<Pick<StylePatternSegmentProps, 'size'>>`
  --baseHeight: ${({ size }) => getBaseHeight(size)}px;

  fill: ${({ theme }) => alpha(theme.palette.text.primary, 0.3)};
  height: calc(var(--baseHeight) / 3.5);
  width: calc(var(--baseHeight) / 3.5);

  &.${groupStylePatternSegmentIconClasses.discrete} {
    fill: ${({ theme }) => theme.palette.text.secondary};
  }
`;

export const StylePatternSegment = forwardRef<
  HTMLDivElement | SVGSVGElement,
  StylePatternSegmentProps
>(({ className, discrete, groupStylePattern, size = 'medium', tabIndex, ...rest }, ref) => {
  const { getStyleName } = useStyleNames();

  if (groupStylePattern === 'kids-service')
    return (
      <StyledBamse
        className={classNames(
          className,
          groupStylePatternSegmentIconClasses.root,
          groupStylePatternSegmentIconClasses.svg,
          {
            [groupStylePatternSegmentIconClasses.discrete]: discrete,
            [groupStylePatternSegmentIconClasses.specialShape]: isSpecialShape(groupStylePattern),
          }
        )}
        ref={ref as RefObject<SVGSVGElement>}
        size={size}
        tabIndex={tabIndex}
        title={getStyleName(groupStylePattern, true)}
        {...(rest as SVGProps<SVGSVGElement>)}
      />
    );

  return (
    <Shape
      className={classNames(
        className,
        groupStylePattern,
        groupStylePatternSegmentIconClasses.root,
        {
          [groupStylePatternSegmentIconClasses.discrete]: discrete,
          [groupStylePatternSegmentIconClasses.specialShape]: isSpecialShape(groupStylePattern),
        }
      )}
      ref={ref as RefObject<HTMLDivElement>}
      size={size}
      sx={{ aspectRatio: getAspectRatioStr(aspectRatioMap[groupStylePattern]) }}
      title={getStyleName(groupStylePattern, true)}
      {...rest}
    />
  );
});

StylePatternSegment.displayName = 'StylePatternSegment';
